// TERRA garden grass — Roblox-style cheap 3D grass. ONE InstancedBufferGeometry of low-poly blades + a
// custom ShaderMaterial = ONE draw call. Placement, ground-conform, and the band gate all happen in the
// VERTEX shader by sampling the shared heightmap (heighttex.ts: R = height, A = grass mask).
//
// P2 CAMERA-FOLLOW RING + WIND: the instance grid is a fixed-size RING that re-centers on the camera focus
// each frame. Each instance's world CELL = uCamCell + aCell, and its jitter / height / yaw / wind phase are
// all derived from that world-cell id — so a blade is a STABLE function of world position (the ring is just
// a moving window over it). That is what stops the field "swimming" as you move.
//
// P3 POLISH: each tuft is now a CROSS of sub-blades (baked, rotated around Y) so grass presents area from any
// angle instead of vanishing edge-on; per-vertex normals drive a soft half-lambert SUN shade; and coarse /
// small screens get a smaller, thinner-crossed ring while prefers-reduced-motion freezes the wind.

import * as THREE from "three";
import type { TerraField } from "./field";
import { GLSL_NOISE } from "./material";
import { CLOUD_COMMON, cloudSlabY, type CloudParams } from "../env/clouds";
import { fogUniforms } from "../env/fog";

export interface GrassHandle {
  mesh: THREE.Mesh;
  /** Per-frame: re-center the ring on the focus + scale it to camera distance + drive wind. */
  setFrame(focusX: number, focusZ: number, camX: number, camY: number, camZ: number,
           time: number, windX: number, windZ: number): void;
  /** Per-frame cloud drift (world-XZ offset), SAME value the terra material gets, so the grass's cloud
   *  shadow tracks the terrain's underneath it. */
  setCloudWind(x: number, z: number): void;
  /** Live-tune a scalar uniform by name (DEV panel). */
  setUniform(name: string, value: number): void;
}

// --- device gates (evaluated once; grass is a pure garnish so it degrades gracefully) ---
function mql(q: string): boolean {
  return typeof matchMedia !== "undefined" && matchMedia(q).matches;
}
// touch devices get the lighter ring. (NOT a width check — the ring is world-space, and a narrow desktop
// window already renders fewer pixels, so it needs no downgrade; width would wrongly demote small windows.)
const MOBILE = mql("(pointer: coarse)");
const REDUCED_MOTION = mql("(prefers-reduced-motion: reduce)");

const GRID = MOBILE ? 240 : 360;   // blades per side of the ring (mobile trims count + ring size)
const CROSS = MOBILE ? 2 : 3;      // sub-blades per tuft — cross-blades read full from any yaw
const BASE_CELL = 10;              // studs between tufts at the closest LOD (FP / ground camera)
const REF = 900;                   // camera-to-focus distance (studs) at which the base cell applies
const GROUND_Y = 200;              // pad-plane height (topY(0)) — the focus's ~ground level for the distance calc

// one tuft = `nCross` tapered blades sharing a base, each rotated around Y so the tuft has area from every
// direction. Local space: x ∈ [-1,1] (tapers to 0 at the tip), y ∈ [0,1]; the rotation folds x into x/z.
// Per-vertex NORMAL is the blade-plane normal (horizontal), for the fragment sun shade.
function bladeGeometry(nCross: number): THREE.BufferGeometry {
  const base = [-1.0, 0.0, 1.0, 0.0, -0.5, 0.5, 0.5, 0.5, 0.0, 1.0]; // 5 (x,y) verts of one tapered blade
  const baseIdx = [0, 2, 1, 1, 2, 3, 2, 4, 3];
  const pos: number[] = [], nrm: number[] = [], idx: number[] = [];
  for (let k = 0; k < nCross; k++) {
    const phi = (k * Math.PI) / nCross;    // spread over 180° (a blade is symmetric ⇒ 180° covers all yaws)
    const c = Math.cos(phi), s = Math.sin(phi);
    for (let v = 0; v < 5; v++) {
      const x = base[v * 2], y = base[v * 2 + 1];
      pos.push(x * c, y, x * s);           // rotate the width axis into the XZ plane
      nrm.push(-s, 0.0, c);                // face normal ⟂ this blade's plane
    }
    for (const i of baseIdx) idx.push(i + k * 5);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(nrm), 3));
  g.setIndex(idx);
  return g;
}

export function buildGrass(
  f: TerraField,
  heightTex: THREE.DataTexture,
  hMin: number,
  hMax: number,
  sun?: THREE.Vector3,
  seed = 0,
  cloud?: CloudParams,
): GrassHandle {
  const base = bladeGeometry(CROSS);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = base.index;
  geo.attributes.position = base.attributes.position;
  geo.attributes.normal = base.attributes.normal;
  const n = GRID * GRID;
  const aCell = new Float32Array(n * 2); // ring offset, CENTERED (-GRID/2 .. GRID/2)
  for (let i = 0; i < n; i++) {
    aCell[i * 2] = (i % GRID) - GRID / 2;
    aCell[i * 2 + 1] = Math.floor(i / GRID) - GRID / 2;
  }
  geo.setAttribute("aCell", new THREE.InstancedBufferAttribute(aCell, 2));
  geo.instanceCount = n;

  const sunDir = (sun ? sun.clone() : new THREE.Vector3(0.4, 0.7, 0.5)).normalize();
  const uniforms = {
    uHeight: { value: heightTex },
    uFieldMin: { value: new THREE.Vector2(f.minX, f.minZ) },
    uFieldSize: { value: new THREE.Vector2(f.maxX - f.minX, f.maxZ - f.minZ) },
    uHMin: { value: hMin },
    uHMax: { value: hMax },
    uCell: { value: BASE_CELL },               // set per frame — scales with camera distance (LOD)
    uCamCell: { value: new THREE.Vector2() },  // floor(focus/cell), set per frame → ring follows the camera
    uRing: { value: (GRID / 2) * BASE_CELL },  // ring radius (studs) for the outer sink-fade — set per frame
    uBlade: { value: 16.0 },
    uBladeW: { value: 2.4 },
    uTime: { value: 0 },
    uWindDir: { value: new THREE.Vector2(1, 0.3) },
    uWindStr: { value: 3.0 },
    uMotion: { value: REDUCED_MOTION ? 0.0 : 1.0 }, // 0 ⇒ static grass for prefers-reduced-motion
    uSunDir: { value: sunDir },
    // color: the terrain grass' OWN display color (measured post-tone-map, sRGB), so blades match the
    // ground they grow on and never recolor it. A root→tip shade multiplier adds depth.
    uGroundColor: { value: new THREE.Vector3(0.345, 0.475, 0.255) },
    uBright: { value: 1.21 }, // lifts the shade average so blades ≈ ground brightness (re-nulled for the gamma below)
    uRootMul: { value: 0.7 },
    uTipMul: { value: 1.08 },
    uSunStr: { value: 0.14 }, // half-lambert amplitude (±); softened so the blade-to-blade sparkle doesn't out-busy the smooth ground
    // TERRAIN-MATCH: the ground the grass sits on is NOT flat color — it's mottled and cloud-shadowed. Re-run
    // the terrain's own macro mottling + cloud shadow at each blade's world position (same seed/scale/params)
    // so the grass tracks the light/dark of the ground beneath it instead of reading as a smooth uniform patch.
    uSeed: { value: (seed % 4096) * 0.37 }, // matches terraMaterial's uSeed
    uMottle: { value: 3.0 },                // matches terraMaterial's uMottleStrength (DEV-tunable)
    uGroundGamma: { value: 0.417 },         // 1/2.4: sRGB-compress the (linear) mottle/band so its display CONTRAST matches the ACES-tonemapped terrain
    uCloudCoverage: { value: cloud?.coverage ?? 0.4 },
    uCloudScale: { value: cloud?.scale ?? 3200 },
    uCloudWind: { value: new THREE.Vector2() }, // set per frame (same drift as terra + sky)
    uCloudSlabY: { value: cloud ? cloudSlabY(cloud) : 3400 },
    uCloudShadowK: { value: cloud?.shadowK ?? 0 },
    uEvolve: { value: 1 },                  // cloud churn — matches terra/sky default
  };
  Object.assign(uniforms, fogUniforms); // share the aerial-fog uniforms (BY REFERENCE) so blades fade into the atmosphere like the terrain

  const mat = new THREE.ShaderMaterial({
    uniforms,
    side: THREE.DoubleSide,
    vertexShader: /* glsl */ `
      uniform sampler2D uHeight;
      uniform vec2 uFieldMin, uFieldSize, uCamCell, uWindDir, uCloudWind;
      uniform float uHMin, uHMax, uCell, uRing, uBlade, uBladeW, uTime, uWindStr, uMotion;
      uniform float uSeed, uMottle, uCloudCoverage, uCloudScale, uCloudSlabY, uCloudShadowK, uEvolve, uGroundGamma;
      uniform vec3 uSunDir;
      attribute vec2 aCell;
      varying float vY;
      varying vec3 vNormal;
      varying float vGround;   // terrain-matched brightness (mottle × cloud shadow) at the blade's world XZ
      varying vec3 vFogWorldPos; // blade world position → aerial fog in the fragment
      float hash21(vec2 p) { p = fract(p * vec2(123.34, 345.45)); p += dot(p, p + 34.345); return fract(p.x * p.y); }
      mat2 rot(float a) { float s = sin(a), c = cos(a); return mat2(c, -s, s, c); }
      ${GLSL_NOISE}
      ${CLOUD_COMMON}
      void main() {
        vec2 cellId = uCamCell + aCell;          // world-cell id — STABLE per world cell (no swimming)
        vec2 cellWorld = cellId * uCell;
        float h1 = hash21(cellId), h2 = hash21(cellId + 37.0);
        vec2 wxz = cellWorld + (vec2(h1, h2) - 0.5) * uCell * 0.9; // intra-cell jitter (stable)

        vec2 uv = (wxz - uFieldMin) / uFieldSize;
        vec4 h = texture2D(uHeight, clamp(uv, 0.0, 1.0));
        float inField = step(0.0, uv.x) * step(uv.x, 1.0) * step(0.0, uv.y) * step(uv.y, 1.0);
        float alive = step(0.5, h.a) * inField;
        float ground = mix(uHMin, uHMax, h.r);

        // TERRAIN-MATCH color: re-run the ground's OWN macro mottling + height micro-band + cloud shadow at
        // this blade's world XZ (same formulae/params as terra/material.ts, so they ALIGN with the terrain
        // underneath), and carry it as a brightness multiplier — so the blade rides the ground's light/dark.
        float mott = tfbm(wxz * (1.0 / 340.0) + uSeed);
        float mottF = 0.82 + 0.36 * (0.5 + (mott - 0.5) * uMottle);
        float bandF = 0.94 + 0.12 * tvn(vec2(ground * 0.021, uSeed * 7.0)); // height micro-band (material.ts:246)
        vec2 projXZ = wxz + uSunDir.xz * ((uCloudSlabY - ground) / max(uSunDir.y, 0.15));
        float cov = cloudCover(projXZ, uCloudCoverage, uCloudScale, uCloudWind, uTime * uEvolve);
        float cloudF = 1.0 - uCloudShadowK * smoothstep(0.05, 0.55, cov);
        // the terrain applies mottle/band/cloud as LINEAR multipliers then ACES+sRGB-compresses them; the grass
        // outputs DISPLAY space, so raise the product to the sRGB gamma → its contrast matches the tonemapped
        // ground (else the grass mottle reads ~2× higher-contrast / blotchier than the smooth terrain).
        vGround = pow(max(mottF * bandF * cloudF, 0.0), uGroundGamma);

        float len = mix(0.7, 1.0, h2);
        vec3 p = position;
        mat2 yaw = rot(h1 * 6.2831);              // per-tuft random spin (whole cross rotates together)
        vec2 xz = yaw * (p.xz * uBladeW);
        vec2 nxz = yaw * normal.xz;
        vNormal = normalize(vec3(nxz.x, normal.y, nxz.y));

        // wind: phase from world XZ (neighbors out of phase → traveling waves), amp = height² * length.
        // uMotion gates it to 0 for prefers-reduced-motion.
        float bend = sin(dot(wxz, uWindDir) * 0.05 + uTime * 1.6) * uWindStr * uMotion * (p.y * p.y) * len;

        vec3 world = vec3(wxz.x + xz.x, ground + p.y * uBlade * len, wxz.y + xz.y);
        world.xz += uWindDir * bend;

        // outer-ring sink: fade blades into the ground toward the ring edge (hide the hard cutoff)
        float distC = length(wxz - uCamCell * uCell);
        float fade = 1.0 - smoothstep(uRing * 0.72, uRing * 0.92, distC);
        world.y = mix(ground, world.y, fade);
        vFogWorldPos = world;                     // real world pos for aerial fog (before the cull-offset below)

        world.y += (1.0 - alive) * (-1e6);       // cull off-band → clip before raster
        vY = p.y;
        gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uGroundColor, uSunDir;
      uniform float uBright, uRootMul, uTipMul, uSunStr;
      // aerial fog (shared fogUniforms by reference) — same model as terra/material.ts so blades fade into the atmosphere
      uniform float uFogAerial, uFogDensity, uFogHeightRef, uFogHeightFalloff, uFogGlare, uFogGlareG, uFogMax, uFogStorm;
      uniform samplerCube uFogSky;
      uniform vec3 uFogSunDir, uFogSunColor;
      varying float vY;
      varying vec3 vNormal;
      varying float vGround;
      varying vec3 vFogWorldPos;
      void main() {
        // soft SUN shade: flip the (baked, horizontal) blade normal to the visible face, half-lambert so thin
        // blades never go fully dark, ± uSunStr around 1.0 (so the average is unchanged ⇒ no terrain recolor).
        vec3 N = normalize(vNormal) * (gl_FrontFacing ? 1.0 : -1.0);
        float ndl = dot(N, uSunDir) * 0.5 + 0.5;
        float sun = mix(1.0 - uSunStr, 1.0 + uSunStr, ndl);
        // uGroundColor is already in output (sRGB) space (it's the terrain grass' measured display color),
        // so we output it directly — no tone-map/color-space here, or we'd double-apply and mismatch.
        // vGround = the terrain's own mottle × cloud shadow, so the blades blend into the ground they sit on.
        vec3 col = uGroundColor * uBright * mix(uRootMul, uTipMul, vY * vY) * sun * vGround;
        // AERIAL FOG: height-fog extinction + fade toward the baked sky cubemap + HG sun glow, all in display space
        // (col is already display-encoded) — the exact model terra/material.ts uses, so grass fades WITH the ground.
        if (uFogAerial >= 0.5) {
          vec3 fogRay = vFogWorldPos - cameraPosition;
          float fogDist = length(fogRay);
          vec3 fogDir = fogRay / max(fogDist, 1e-4);
          float kh = uFogHeightFalloff, ty = fogDir.y * kh;
          float base = uFogDensity * uFogStorm * exp(-(cameraPosition.y - uFogHeightRef) * kh);
          float optical = (abs(ty) > 1e-5) ? base * (1.0 - exp(-fogDist * ty)) / ty : base * fogDist;
          float fogFactor = (1.0 - exp(-max(optical, 0.0))) * uFogMax;
          vec3 fogCol = textureCube(uFogSky, fogDir).rgb;
          float mu = dot(fogDir, uFogSunDir), g = uFogGlareG;
          float hg = (1.0 - g * g) / pow(max(1.0 + g * g - 2.0 * g * mu, 1e-4), 1.5) * 0.07957747;
          fogCol += uFogSunColor * (hg * uFogGlare);
          col = mix(col, fogCol, clamp(fogFactor, 0.0, 1.0));
        }
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  // render BEFORE the (expensive) terrain arena (renderOrder 0): the opaque blades write depth first, so
  // every terrain pixel hidden behind a blade is EARLY-Z rejected — its costly fragment shader never runs,
  // and a dirt-cheap grass fragment takes its place. Dense grass can thus be NET-CHEAPER than bare terrain.
  mesh.renderOrder = -1;

  return {
    mesh,
    setFrame(focusX: number, focusZ: number, camX: number, camY: number, camZ: number,
             time: number, windX: number, windZ: number): void {
      uniforms.uTime.value = time;
      // Elevation/zoom-aware ring: the further the camera is from the focus (high command view), the WIDER
      // and sparser the ring — so it covers what you see instead of a dense spot on the ground. DISCRETE LOD
      // (cell doubles per octave) keeps the world-cell grid stable within a level → no swim except a rare
      // re-place when you cross a zoom threshold.
      const camDist = Math.hypot(camX - focusX, camY - GROUND_Y, camZ - focusZ);
      const level = Math.min(2, Math.max(0, Math.round(Math.log2(Math.max(camDist, 1) / REF))));
      const cell = BASE_CELL * (1 << level); // 10 / 20 / 40 studs
      uniforms.uCell.value = cell;
      uniforms.uRing.value = (GRID / 2) * cell;
      (uniforms.uCamCell.value as THREE.Vector2).set(Math.floor(focusX / cell), Math.floor(focusZ / cell));
      const wl = Math.hypot(windX, windZ) || 1;
      (uniforms.uWindDir.value as THREE.Vector2).set(windX / wl, windZ / wl);
    },
    setCloudWind(x: number, z: number): void {
      (uniforms.uCloudWind.value as THREE.Vector2).set(x, z);
    },
    setUniform(name: string, value: number): void {
      const u = (uniforms as Record<string, { value: unknown }>)[name];
      if (u && typeof u.value === "number") u.value = value;
    },
  };
}
